import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

import { saveBlob } from '@/shared/lib/download';
import type { DragPayload } from './tile-dom';

/**
 * Браузерная половина `tile-dom.ts`. Узлы — DOM-элементы: на вебе `View` и
 * `Pressable` рисуются react-dom'ом, и их `ref` — это сам элемент. Слушатели вешаются
 * руками, потому что react-native-web не пропускает `draggable` и `onDrag*` в DOM.
 */
export type { DragPayload };

export const CAN_DRAG = true;
export const CAN_SNAPSHOT = true;
export const CAN_ZOOM = true;

const MIME = 'application/x-samvision-live';

function el(ref: RefObject<unknown>): HTMLElement | null {
  const node = ref.current as HTMLElement | null;
  return node && typeof node.addEventListener === 'function' ? node : null;
}

/**
 * Перетаскиваемый элемент. `payload: null` — не тащится (пустое окно, окно под
 * зумом, где тянуть мышью значит двигать картинку).
 */
export function useDragSource(
  ref: RefObject<unknown>,
  payload: DragPayload | null,
): void {
  const key = payload ? JSON.stringify(payload) : '';
  useEffect(() => {
    const node = el(ref);
    if (!node || !key) {
      return;
    }
    node.setAttribute('draggable', 'true');
    const onStart = (e: DragEvent) => {
      e.dataTransfer?.setData(MIME, key);
      // Без text/plain Firefox не начинает перетаскивание вовсе.
      e.dataTransfer?.setData('text/plain', '');
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
      }
    };
    node.addEventListener('dragstart', onStart);
    return () => {
      node.removeAttribute('draggable');
      node.removeEventListener('dragstart', onStart);
    };
  }, [ref, key]);
}

/**
 * Курсор над окном или над чем-то внутри него.
 *
 * Не `onHoverIn/Out` у `Pressable`: react-native-web ведёт hover с `contain`, и
 * вложенный `Pressable` (SD/HD, снимок, ✕) при наведении гасит hover родителя —
 * панель инструментов исчезала ровно в тот миг, когда к ней подводили курсор.
 * `pointerenter`/`pointerleave` не всплывают и считают потомков частью элемента.
 */
export function useHoverWithin(ref: RefObject<unknown>): boolean {
  const [hover, setHover] = useState(false);
  useEffect(() => {
    const node = el(ref);
    if (!node) {
      return;
    }
    const enter = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') {
        setHover(true);
      }
    };
    const leave = () => setHover(false);
    node.addEventListener('pointerenter', enter);
    node.addEventListener('pointerleave', leave);
    return () => {
      node.removeEventListener('pointerenter', enter);
      node.removeEventListener('pointerleave', leave);
    };
  }, [ref]);
  return hover;
}

/** Окно, в которое бросают. Возвращает «над ним сейчас тащат» — для подсветки. */
export function useDropTarget(
  ref: RefObject<unknown>,
  onDrop: (payload: DragPayload) => void,
): boolean {
  const [over, setOver] = useState(false);
  const drop = useRef(onDrop);
  drop.current = onDrop;
  useEffect(() => {
    const node = el(ref);
    if (!node) {
      return;
    }
    // dragenter/dragleave приходят и от детей окна; счётчик отличает «ушёл с окна»
    // от «перешёл на подпись внутри него».
    let depth = 0;
    const ours = (e: DragEvent) => !!e.dataTransfer?.types.includes(MIME);
    const onEnter = (e: DragEvent) => {
      if (ours(e)) {
        depth += 1;
        setOver(true);
      }
    };
    const onOver = (e: DragEvent) => {
      if (ours(e)) {
        e.preventDefault();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'move';
        }
      }
    };
    const onLeave = (e: DragEvent) => {
      if (ours(e)) {
        depth = Math.max(0, depth - 1);
        if (depth === 0) {
          setOver(false);
        }
      }
    };
    const onDropEvent = (e: DragEvent) => {
      depth = 0;
      setOver(false);
      const raw = e.dataTransfer?.getData(MIME);
      if (!raw) {
        return;
      }
      e.preventDefault();
      try {
        drop.current(JSON.parse(raw) as DragPayload);
      } catch {
        // Чужие данные — не наше перетаскивание.
      }
    };
    node.addEventListener('dragenter', onEnter);
    node.addEventListener('dragover', onOver);
    node.addEventListener('dragleave', onLeave);
    node.addEventListener('drop', onDropEvent);
    return () => {
      node.removeEventListener('dragenter', onEnter);
      node.removeEventListener('dragover', onOver);
      node.removeEventListener('dragleave', onLeave);
      node.removeEventListener('drop', onDropEvent);
    };
  }, [ref]);
  return over;
}

const MAX_ZOOM = 8;

/**
 * Цифровой зум, как «электронное увеличение» в iVMS: колесо приближает к точке под
 * курсором, тяга мышью двигает приближенную картинку. На сенсорном экране — то же
 * двумя пальцами: развести — приблизить, вести одним — двигать. Трансформация пишется прямо в
 * стиль `<video>`: шестнадцать окон, перерисовывающихся на каждый щелчок колеса, —
 * это заметная задержка, а одно свойство элемента — нет.
 */
export function useDigitalZoom(
  ref: RefObject<unknown>,
  enabled: boolean,
): { scale: number; reset: () => void } {
  const [scale, setScale] = useState(1);
  const view = useRef({ s: 1, x: 0, y: 0 });

  const apply = useCallback(() => {
    const video = el(ref)?.querySelector('video');
    if (video) {
      const { s, x, y } = view.current;
      video.style.transformOrigin = '0 0';
      video.style.transform =
        s === 1 ? '' : `translate(${x}px, ${y}px) scale(${s})`;
    }
  }, [ref]);

  const reset = useCallback(() => {
    view.current = { s: 1, x: 0, y: 0 };
    setScale(1);
    apply();
  }, [apply]);

  useEffect(() => {
    const node = el(ref);
    if (!node || !enabled) {
      return;
    }
    const clamp = () => {
      const v = view.current;
      const w = node.clientWidth;
      const h = node.clientHeight;
      v.x = Math.min(0, Math.max(w - w * v.s, v.x));
      v.y = Math.min(0, Math.max(h - h * v.s, v.y));
    };
    /** Масштаб `target` вокруг точки окна (`clientX`, `clientY`). */
    const zoomAt = (clientX: number, clientY: number, target: number) => {
      const rect = node.getBoundingClientRect();
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      const v = view.current;
      const next = Math.min(MAX_ZOOM, Math.max(1, target));
      // Точка под курсором остаётся под курсором.
      v.x = px - ((px - v.x) * next) / v.s;
      v.y = py - ((py - v.y) * next) / v.s;
      v.s = next;
      if (next === 1) {
        v.x = 0;
        v.y = 0;
      }
      clamp();
      apply();
      setScale(Math.round(next * 10) / 10);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(
        e.clientX,
        e.clientY,
        view.current.s * (e.deltaY < 0 ? 1.2 : 1 / 1.2),
      );
    };
    let pinch: { dist: number; s: number } | null = null;
    let pan: { x: number; y: number } | null = null;
    const distance = (t: TouchList) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinch = { dist: distance(e.touches), s: view.current.s };
        pan = null;
      } else if (e.touches.length === 1 && view.current.s > 1) {
        pan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (pinch && e.touches.length === 2) {
        e.preventDefault();
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        zoomAt(cx, cy, (pinch.s * distance(e.touches)) / pinch.dist);
      } else if (pan && e.touches.length === 1) {
        // Под зумом палец двигает картинку, а не листает страницу.
        e.preventDefault();
        const t0 = e.touches[0];
        view.current.x += t0.clientX - pan.x;
        view.current.y += t0.clientY - pan.y;
        pan = { x: t0.clientX, y: t0.clientY };
        clamp();
        apply();
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        pinch = null;
      }
      if (e.touches.length === 0) {
        pan = null;
      }
    };
    let drag: { x: number; y: number } | null = null;
    const onDown = (e: MouseEvent) => {
      if (view.current.s > 1 && e.button === 0) {
        drag = { x: e.clientX, y: e.clientY };
      }
    };
    const onMove = (e: MouseEvent) => {
      if (!drag) {
        return;
      }
      view.current.x += e.clientX - drag.x;
      view.current.y += e.clientY - drag.y;
      drag = { x: e.clientX, y: e.clientY };
      clamp();
      apply();
    };
    const onUp = () => {
      drag = null;
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: false });
    node.addEventListener('touchend', onTouchEnd);
    node.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [ref, enabled, apply]);

  // Новая картинка (переподключение) — новый `<video>`: зум переносится на него.
  useEffect(() => {
    if (!enabled && view.current.s !== 1) {
      reset();
    }
  }, [enabled, reset]);

  return { scale, reset };
}

/**
 * Снимок кадра из окна в PNG — то, что охрана делает «для отчёта». Кадр берётся в
 * собственном разрешении потока, а не в размере окна.
 */
export function saveSnapshot(node: unknown, filename: string): boolean {
  const video = (node as HTMLElement | null)?.querySelector?.('video');
  if (!video || !video.videoWidth) {
    return false;
  }
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d')?.drawImage(video, 0, 0);
  canvas.toBlob(blob => {
    if (blob) {
      saveBlob(blob, filename);
    }
  }, 'image/png');
  return true;
}
